import React, { useCallback } from 'react';
import { memo } from 'react';
import BaseNode from '../BaseNode';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import OutputIcon from '@mui/icons-material/Output';
import { SliderControl, ActivationSelector } from '../../core/NodeControls';

/**
 * OutputNode represents the output layer of the neural network
 */
const OutputNode = ({ data, id, selected, isConnectable }) => {
  const theme = useTheme();
  
  // Handler to update node data
  const handleDataChange = useCallback((key, value) => {
    if (data.onChange) {
      data.onChange(id, key, value);
    }
  }, [data, id]);
  
  // Generate preview content
  const renderPreview = () => {
    return (
      <>
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 1 }}>
          Prediction Output
        </Typography>
        
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            width: '100%',
            height: '30px',
            background: alpha(theme.palette.success.main, 0.1),
            borderRadius: '4px',
            border: `1px dashed ${alpha(theme.palette.success.main, 0.5)}`,
          }}
        >
          <Box
            sx={{
              width: '10px',
              height: '100%',
              backgroundColor: alpha(theme.palette.success.main, 0.7),
              mr: 0.5,
            }}
          />
          <Box
            sx={{
              width: '10px',
              height: '100%',
              backgroundColor: alpha(theme.palette.success.main, 0.5),
              mr: 0.5,
            }}
          />
          <Box
            sx={{
              width: '10px',
              height: '100%',
              backgroundColor: alpha(theme.palette.success.main, 0.3),
            }}
          />
        </Box>
      </>
    );
  };
  
  // Controls for the node configuration popover
  const renderControls = () => {
    return (
      <>
        <SliderControl
          value={data.units || 1}
          onChange={(value) => handleDataChange('units', value)}
          min={1}
          max={100}
          step={1}
          label="Output Units"
          tooltip="Number of output features"
          marks
        />
        
        <ActivationSelector
          value={data.activation || 'linear'}
          onChange={(value) => handleDataChange('activation', value)}
          label="Activation Function"
        />
      </>
    );
  };
  
  return (
    <BaseNode
      data={data}
      id={id}
      selected={selected}
      isConnectable={isConnectable}
      icon={<OutputIcon fontSize="small" />}
      nodeType="output"
      preview={renderPreview()}
      controls={renderControls()}
      multipleInputs={false}
      multipleOutputs={false}
      disableOutputHandle={true} // Disable the output handle
      style={{
        borderStyle: 'dashed',
      }}
      onDelete={data.onDelete}
    />
  );
};

export default memo(OutputNode); 